/**
 * AEGIS-7 demo seed — platform-wide intelligence data.
 *
 * Populates aegis_actions (append-only ledger) and aegis_predictions
 * (forward-looking forecasts) so every dashboard has real signal on first
 * load. All records are scoped to the kairos-demo tenant and mirror the
 * schema in Kairos-AEGIS7-AI-Intelligence-Standard.docx.
 */
import { DEMO_TENANT_ID } from './tenantDefaults';

const today = new Date();
const hoursAgo = (h) => new Date(today.getTime() - h * 3600000).toISOString();
const hoursAhead = (h) => new Date(today.getTime() + h * 3600000).toISOString();
const daysAhead = (d) => new Date(today.getTime() + d * 86400000).toISOString();

const base = (extra = {}) => ({
  tenantId: DEMO_TENANT_ID,
  createdAt: hoursAgo(24),
  createdBy: 'aegis_auto',
  updatedAt: hoursAgo(1),
  updatedBy: 'aegis_auto',
  isDeleted: false,
  softDeleted: false,
  version: 1,
  source: 'aegis_seed',
  ...extra,
});

// ── aegis_actions (10 rows: auto-executed, approved, insight_only) ──────
export const SEED_AEGIS_ACTIONS = [
  {
    id: 'act-001', module: 'tms', actionType: 'carrier_swap', trigger: 'anomaly',
    confidence: 0.92, autonomy: 'auto', status: 'executed', aegisMode: 'primary',
    rationale: 'Primary carrier SWFT projected to decline tender on Chicago → Dallas based on 2 recent declines on this lane; backup Werner accepting within 12min average.',
    proposedAction: { swapTo: 'WERN', loadId: 'load-00003' },
    executedAction: { swappedTo: 'WERN', executedAt: hoursAgo(6) },
    affectedEntityId: 'load-00003', affectedEntityType: 'load',
    createdBy: 'aegis_auto',
    ...base({ createdAt: hoursAgo(6) }),
  },
  {
    id: 'act-002', module: 'tms', actionType: 'customer_eta_update', trigger: 'threshold_breach',
    confidence: 0.88, autonomy: 'auto', status: 'executed', aegisMode: 'primary',
    rationale: 'Load WC-10712 delayed at Louisville due to NS mechanical issue; predicted 4h late. Proactive customer notification sent.',
    proposedAction: { channel: 'email', template: 'eta_update' },
    executedAction: { channel: 'email', sentAt: hoursAgo(2), recipients: ['ops@customer.com'] },
    affectedEntityId: 'load-00013', affectedEntityType: 'load',
    ...base({ createdAt: hoursAgo(2) }),
  },
  {
    id: 'act-003', module: 'cx', actionType: 'sla_escalation', trigger: 'threshold_breach',
    confidence: 0.74, autonomy: 'approved', status: 'pending',
    rationale: 'Wild Fork Foods has 2 at-risk deliveries today within a 4h window. SLA breach probability 74% — recommend escalating to account manager.',
    proposedAction: { assignee: 'user-004', escalationLevel: 2 },
    affectedEntityId: 'cu-005', affectedEntityType: 'customer',
    aegisMode: 'primary',
    ...base({ createdAt: hoursAgo(0.5) }),
  },
  {
    id: 'act-004', module: 'iot', actionType: 'cold_chain_intervention', trigger: 'anomaly',
    confidence: 0.96, autonomy: 'auto', status: 'executed', aegisMode: 'primary',
    rationale: 'Tracker WM-TRK-01003 trending toward 75°F within 90min; threshold is 72°F. Driver alerted and dispatch notified.',
    proposedAction: { alertDriver: true, notifyDispatch: true },
    executedAction: { alertedAt: hoursAgo(1), channel: 'sms' },
    affectedEntityId: 'WM-TRK-01003', affectedEntityType: 'sensor',
    ...base({ createdAt: hoursAgo(1) }),
  },
  {
    id: 'act-005', module: 'ims', actionType: 'reorder_trigger', trigger: 'threshold_breach',
    confidence: 0.81, autonomy: 'approved', status: 'pending',
    rationale: 'SKU-4421 projected to stock out in 6 days at current velocity. Lead time 8 days — recommend placing order today.',
    proposedAction: { sku: 'SKU-4421', quantity: 480, supplier: 'sup-001' },
    affectedEntityId: 'SKU-4421', affectedEntityType: 'inventory_item',
    aegisMode: 'primary',
    ...base({ createdAt: hoursAgo(4) }),
  },
  {
    id: 'act-006', module: 'fleet', actionType: 'maintenance_scheduled', trigger: 'anomaly',
    confidence: 0.89, autonomy: 'auto', status: 'executed', aegisMode: 'primary',
    rationale: 'Unit WM-1003 showing elevated brake-wear signal across 3 recent inspection cycles. Preventive brake pad replacement scheduled.',
    proposedAction: { vehicleId: 'fv-003', type: 'repair' },
    executedAction: { workOrderId: 'fm-001', executedAt: hoursAgo(12) },
    affectedEntityId: 'fv-003', affectedEntityType: 'vehicle',
    ...base({ createdAt: hoursAgo(12) }),
  },
  {
    id: 'act-007', module: 'wms', actionType: 'pick_task_rebalance', trigger: 'threshold_breach',
    confidence: 0.77, autonomy: 'approved', status: 'executed', aegisMode: 'primary',
    rationale: 'Zone B pick pace 18% below wave completion threshold. Reassigned 2 pickers from Zone D to close the gap.',
    proposedAction: { from: 'zone_d', to: 'zone_b', count: 2 },
    executedAction: { executedAt: hoursAgo(3), approvedBy: 'user-006' },
    affectedEntityId: 'wave-240415', affectedEntityType: 'shipment',
    ...base({ createdAt: hoursAgo(3) }),
  },
  {
    id: 'act-008', module: 'tms', actionType: 'margin_flag', trigger: 'threshold_breach',
    confidence: 0.55, autonomy: 'insight_only', status: 'pending',
    rationale: 'Quote QT-10005 margin is 10.7% — below historical win band for Air mode on this lane (median 14.5%). Consider re-pricing or adding fuel surcharge.',
    proposedAction: { quoteId: 'qt-005', suggestedRate: 12200 },
    affectedEntityId: 'qt-005', affectedEntityType: 'load',
    aegisMode: 'primary',
    ...base({ createdAt: hoursAgo(5) }),
  },
  {
    id: 'act-009', module: 'cx', actionType: 'exception_comm', trigger: 'anomaly',
    confidence: 0.71, autonomy: 'approved', status: 'pending',
    rationale: 'Load WC-10745 delivered 2h early. Drafted customer notification highlighting the on-time win.',
    proposedAction: { channel: 'email', draft: 'Early delivery confirmation' },
    affectedEntityId: 'load-00046', affectedEntityType: 'load',
    aegisMode: 'primary',
    ...base({ createdAt: hoursAgo(7) }),
  },
  {
    id: 'act-010', module: 'tms', actionType: 'rate_adjustment', trigger: 'user_request',
    confidence: 0.48, autonomy: 'insight_only', status: 'pending',
    rationale: 'Spot rate on Dallas → Atlanta drifted -4% over trailing 14 days. Insight only — decision still belongs to the dispatcher.',
    proposedAction: { lane: 'DAL-ATL', suggestion: 'reduce_target_rate_4pct' },
    affectedEntityId: null, affectedEntityType: 'lane',
    aegisMode: 'primary',
    ...base({ createdAt: hoursAgo(18) }),
  },
];

// ── aegis_predictions (5 rows: mixed severity + modules) ─────────────────
export const SEED_AEGIS_PREDICTIONS = [
  {
    id: 'pred-001', module: 'iot', predictionType: 'cold_chain_breach',
    entityId: 'WM-TRK-01003', entityType: 'sensor',
    predictedAt: hoursAhead(2),
    confidence: 0.92, severity: 'critical',
    summary: 'Tracker WM-TRK-01003 on load WC-10703 projected to exceed 72°F within 2 hours.',
    factors: [
      { label: 'Ambient temp rising 4°F / hour', weight: 0.45 },
      { label: 'Reefer door open 3×', weight: 0.3 },
      { label: 'Historical breach pattern', weight: 0.25 },
    ],
    recommendedActions: [
      { actionType: 'alert_driver', rationale: 'Notify driver to check reefer setpoint.', urgency: 'immediate' },
      { actionType: 'customer_notify', rationale: 'Send proactive breach-risk email to consignee.', urgency: 'immediate' },
    ],
    status: 'active',
    ...base({ createdAt: hoursAgo(0.5) }),
  },
  {
    id: 'pred-002', module: 'cx', predictionType: 'sla_breach',
    entityId: 'cu-005', entityType: 'customer',
    predictedAt: hoursAhead(36),
    confidence: 0.81, severity: 'high',
    summary: 'Wild Fork Foods SLA compliance projected to drop below 90% within 36 hours based on at-risk delivery trend.',
    factors: [
      { label: '2 at-risk deliveries today', weight: 0.4 },
      { label: 'Last-week OTP 86%', weight: 0.3 },
      { label: 'Carrier SWFT performance decline', weight: 0.3 },
    ],
    recommendedActions: [
      { actionType: 'escalate_am', rationale: 'Alert account manager to pre-empt customer complaint.', urgency: 'today' },
      { actionType: 'carrier_review', rationale: 'Re-rank preferred carriers for this customer.', urgency: 'this_week' },
    ],
    status: 'active',
    ...base({ createdAt: hoursAgo(2) }),
  },
  {
    id: 'pred-003', module: 'ims', predictionType: 'stockout',
    entityId: 'SKU-4421', entityType: 'inventory_item',
    predictedAt: daysAhead(6),
    confidence: 0.79, severity: 'high',
    summary: 'SKU-4421 at Dallas DC projected to stock out in 6 days at current velocity. Lead time 8 days.',
    factors: [
      { label: 'Current on-hand 320u', weight: 0.35 },
      { label: '14d avg velocity 54u/day', weight: 0.4 },
      { label: 'No open PO', weight: 0.25 },
    ],
    recommendedActions: [
      { actionType: 'create_po', rationale: 'Place PO for 480 units with preferred supplier today.', urgency: 'today' },
    ],
    status: 'active',
    ...base({ createdAt: hoursAgo(4) }),
  },
  {
    id: 'pred-004', module: 'fleet', predictionType: 'equipment_failure',
    entityId: 'fv-004', entityType: 'vehicle',
    predictedAt: daysAhead(10),
    confidence: 0.68, severity: 'medium',
    summary: 'Unit WM-1004 projected to need DOT inspection remediation within 10 days — brake system wear pattern matches prior failure cluster.',
    factors: [
      { label: '321,780 mi odometer', weight: 0.35 },
      { label: 'Last inspection 88 days ago', weight: 0.3 },
      { label: 'Insurance expiring in 45 days', weight: 0.35 },
    ],
    recommendedActions: [
      { actionType: 'schedule_maintenance', rationale: 'Book preventive brake service before inspection.', urgency: 'this_week' },
    ],
    status: 'active',
    ...base({ createdAt: hoursAgo(8) }),
  },
  {
    id: 'pred-005', module: 'tms', predictionType: 'late_delivery',
    entityId: 'load-00003', entityType: 'load',
    predictedAt: hoursAhead(18),
    confidence: 0.73, severity: 'high',
    summary: 'Load WC-10703 (DAL → LAX) projected to arrive 4h behind schedule due to driver HOS pressure and weather on I-40.',
    factors: [
      { label: 'Driver HOS 2.3h available', weight: 0.4 },
      { label: 'Winter advisory Albuquerque segment', weight: 0.3 },
      { label: 'Late pickup by 45min', weight: 0.3 },
    ],
    recommendedActions: [
      { actionType: 'customer_eta_update', rationale: 'Send proactive delay notification to consignee.', urgency: 'immediate' },
      { actionType: 'driver_reset_plan', rationale: 'Plan 8h reset near Flagstaff to stay compliant.', urgency: 'immediate' },
    ],
    status: 'active',
    ...base({ createdAt: hoursAgo(1) }),
  },
];

export const AEGIS_SEED_SUMMARY = {
  aegis_actions: SEED_AEGIS_ACTIONS.length,
  aegis_predictions: SEED_AEGIS_PREDICTIONS.length,
};
